import {
  PaymentElement,
  Elements,
  useStripe,
  useElements,
} from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import React, { useEffect, useState } from 'react';
import { useMediaQuery } from 'react-responsive';
import styled, { useTheme } from 'styled-components';
import Loading from 'components/Loading';
import { POSModal, POSButton } from 'components/POS';
import { usePOSRequest } from './gql/queries/usePOSRequest';

const FormWrapper = styled.form`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  flex-grow: 1;
  gap: 20px;
`;

const PaymentElementWrapper = styled.div`
  flex: 1;

  /* Override Stripe element styles for dark theme */
  ${(props) =>
    props.theme.name === 'dark' &&
    `
    .StripeElement {
      background: rgba(255, 255, 255, 0.05);
      border-radius: 8px;
      padding: 12px;
    }
  `}
`;

const ErrorText = styled.div`
  font-family: 'Barlow Condensed', sans-serif;
  font-size: 16px;
  color: ${(props) => props.theme.colors?.error || '#EF4444'};
  text-align: center;
  margin-bottom: 16px;
`;

const LoadingWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 20px;
`;

const CheckoutFormStripe = ({ amount, paymentIntent }) => {
  const stripe = useStripe();
  const elements = useElements();

  const [errorMessage, setErrorMessage] = useState(null);
  const [paymentInProgress, setPaymentInProgress] = useState(false);

  const handleSubmit = async (event) => {
    setPaymentInProgress(true);
    event.preventDefault();

    if (elements == null) {
      return;
    }

    // Trigger form validation and wallet collection
    const { error: submitError } = await elements.submit();
    if (submitError) {
      setPaymentInProgress(false);
      // Show error to your customer
      setErrorMessage(submitError.message);
      return;
    }

    const { error } = await stripe.confirmPayment({
      //`Elements` instance that was used to create the Payment Element
      elements,
      clientSecret: paymentIntent,
      confirmParams: {
        return_url: window.location.href,
      },
    });

    setPaymentInProgress(false);
    if (error) {
      // This point will only be reached if there is an immediate error when
      // confirming the payment. Show error to your customer (for example, payment
      // details incomplete)
      setErrorMessage(error.message);
      setPaymentInProgress(false);
    } else {
      // Your customer will be redirected to your `return_url`. For some payment
      // methods like iDEAL, your customer will be redirected to an intermediate
      // site first to authorize the payment, then redirected to the `return_url`.
    }
  };

  return (
    <FormWrapper onSubmit={handleSubmit}>
      <PaymentElementWrapper>
        <PaymentElement />
      </PaymentElementWrapper>
      <div>
        {/* Show error message to your customers */}
        {errorMessage && <ErrorText>{errorMessage}</ErrorText>}
        {paymentInProgress ? (
          <LoadingWrapper>
            <Loading />
          </LoadingWrapper>
        ) : (
          <POSButton
            variant="primary"
            type="submit"
            style={{
              width: '100%',
              padding: '20px',
              fontSize: '24px',
              fontWeight: 700,
            }}
          >
            Charge ${(amount / 100).toFixed(2)}
          </POSButton>
        )}
      </div>
    </FormWrapper>
  );
};

// Make sure to call `loadStripe` outside of a component’s render to avoid
// recreating the `Stripe` object on every render.
const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PAYMENT_KEY);

const BodyWrapper = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 400px;
`;

const PaymentLoadingWrapper = styled.div`
  display: flex;
  flex: 1;
  align-items: center;
  justify-content: center;
`;

export const CheckoutForm = ({
  isVisible,
  setIsVisible,
  amount,
  accountId,
}) => {
  const theme = useTheme();
  const posRequest = usePOSRequest();

  const isMobile = useMediaQuery({ query: '(max-width: 768px)' });

  const [paymentIntent, setPaymentIntent] = useState(null);

  useEffect(() => {
    const fetchPaymentIntent = async () => {
      const result = await posRequest({
        account_id: accountId,
        amount: amount,
      });

      if (result) {
        setPaymentIntent(result.data.posRequest);
      }
    };

    if (isVisible) fetchPaymentIntent();
  }, [accountId, amount, posRequest, isVisible]);

  // Stripe appearance config - adapts to theme
  const isDarkTheme = theme.name === 'dark';
  const appearance = {
    theme: isDarkTheme ? 'night' : 'stripe',
    variables: {
      colorPrimary: theme.colors?.primary || '#FA4616',
      colorBackground: isDarkTheme ? 'rgba(255, 255, 255, 0.05)' : '#ffffff',
      colorText: isDarkTheme ? '#ffffff' : '#000033',
      colorDanger: theme.colors?.error || '#EF4444',
      fontFamily: "'Barlow Condensed', sans-serif",
      fontSize: isMobile ? '18px' : '20px',
      fontWeight: 500,
      lineHeight: '28px',
      spacingUnit: '5px',
      borderRadius: '8px',
    },
    rules: {
      '.Input': {
        backgroundColor: isDarkTheme ? 'rgba(255, 255, 255, 0.05)' : '#ffffff',
        border: `1px solid ${
          isDarkTheme ? 'rgba(255, 255, 255, 0.15)' : 'rgba(0, 0, 0, 0.15)'
        }`,
        padding: '14px',
      },
      '.Input:focus': {
        borderColor: theme.colors?.primary || '#FA4616',
        boxShadow: 'none',
      },
      '.Label': {
        color: isDarkTheme ? '#94A3B8' : '#64748B',
        fontSize: '14px',
        fontWeight: '600',
        marginBottom: '8px',
      },
    },
  };

  const options = {
    mode: 'payment',
    amount: amount,
    currency: 'usd',
    appearance: appearance,
  };

  return (
    <POSModal
      isVisible={isVisible}
      onClose={() => setIsVisible(false)}
      title="Key in Card Number"
    >
      <BodyWrapper>
        {paymentIntent ? (
          <Elements stripe={stripePromise} options={options}>
            <CheckoutFormStripe amount={amount} paymentIntent={paymentIntent} />
          </Elements>
        ) : (
          <PaymentLoadingWrapper>
            <Loading />
          </PaymentLoadingWrapper>
        )}
      </BodyWrapper>
    </POSModal>
  );
};
